// ── ViewSOModal.jsx ───────────────────────────────────────────────────────────
// Drop this component into your ScrapLoadingForm.jsx
// Usage: <ViewSOModal row={selectedSORow} onClose={() => setSelectedSORow(null)} />
//
// Also add this button in the Actions column of your table:
// <button onClick={() => setSelectedSORow(row)} ...><FileText size={13}/> View SO</button>
//
// And add state: const [selectedSORow, setSelectedSORow] = useState(null);

import React, { useState } from "react";
import { X, Download, FileText, ShoppingCart } from "lucide-react";

// ── helpers (copy from your existing file if not already global) ──────────────
const esc = (s) =>
    String(s ?? "").replace(/[&<>"']/g, (c) =>
        ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c])
    );

const fmt = (n) =>
    Number(n)
        ? Number(n).toLocaleString("en-PH", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
        : "0.00";

// Field accessors for ScrapLoadingForm row → map to SO fields
// The scrap items become the SO line items
const fScrap = {
    controlNo:    (r) => r.controlNo    || r.ControlNo    || "",
    formDate:     (r) => r.formDate     || r.FormDate     || "",
    customerName: (r) => r.customerName || r.CustomerName || "",
    preparedBy:   (r) => r.preparedBy   || r.PreparedBy   || "",
    checkedBy:    (r) => r.checkedBy    || r.CheckedBy    || "",
    receivedBy:   (r) => r.receivedBy   || r.ReceivedBy   || "",
    remarks:      (r) => r.remarks      || r.Remarks      || "",
    items:        (r) => r.items        || r.Items        || [],
};

// ── SO Number formatter: pad to 6 digits ──────────────────────────────────────
const formatSoNo = (controlNo) => String(parseInt(controlNo, 10) || 0).padStart(6, "0");

// ── Compute totals from scrap items ──────────────────────────────────────────
const computeSoTotals = (items) => {
    const totalSales = items.reduce((sum, i) => {
        const amt = parseFloat(i.amount) || (parseFloat(i.quantity) * parseFloat(i.unitPrice)) || 0;
        return sum + amt;
    }, 0);
    return { totalSales, discount: 0, totalPayable: totalSales };
};

// ── SO Document Component (matches printed format in photo) ───────────────────
const SalesOrderDocument = ({ data, soOverrides }) => {
    const items  = fScrap.items(data);
    const soNo   = soOverrides?.soNo   || formatSoNo(fScrap.controlNo(data));
    const soDate = soOverrides?.soDate || fScrap.formDate(data);

    // Map scrap items → SO line items
    const lineItems = items.map((item) => ({
        qty:         item.quantity   || item.Quantity   || "",
        uom:         item.uom        || item.Uom        || item.UOM || "KGS",
        itemCode:    item.scrapCodeName || item.ScrapCodeName || "",
        description: item.scrapCodeName || item.ScrapCodeName || "",
        unitPrice:   item.unitPrice  || item.UnitPrice  || "",
        amount:      item.amount     || item.Amount     ||
                     (parseFloat(item.quantity) * parseFloat(item.unitPrice)) || "",
    }));

    const { totalSales, discount, totalPayable } = computeSoTotals(lineItems);

    // Show at least 8 rows (blank rows fill the rest like the printed form)
    const MIN_ROWS = 8;
    const displayItems = [
        ...lineItems,
        ...Array.from({ length: Math.max(0, MIN_ROWS - lineItems.length) }, () => ({})),
    ];

    return (
        <div style={{ fontFamily: "'Times New Roman', Times, serif", background: "#fff", border: "1px solid #ccc" }}>

            {/* ── COMPANY HEADER ── */}
            <div style={{
                textAlign: "center",
                padding: "10px 24px 6px",
                borderBottom: "2.5px solid #000",
            }}>
                <div style={{ fontSize: "13px", fontWeight: "900", letterSpacing: "0.08em", textTransform: "uppercase", color: "#000" }}>
                    MEGA SOFT HYGIENIC PRODUCTS, INC.
                </div>
            </div>

            {/* ── TITLE + SO NO ── */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", padding: "8px 24px 6px" }}>
                <div style={{ fontSize: "22px", fontWeight: "900", fontStyle: "italic", color: "#000", letterSpacing: "0.02em" }}>
                    SALES ORDER
                </div>
                <div style={{ textAlign: "right", display: "flex", alignItems: "center", gap: "6px" }}>
                    <span style={{ fontSize: "11px", fontWeight: "700", color: "#555" }}>SO No</span>
                    <span style={{ fontSize: "11px", fontWeight: "900", color: "#166534" }}>VS</span>
                    <span style={{
                        fontSize: "22px", fontWeight: "900", color: "#166534",
                        letterSpacing: "2px", fontFamily: "monospace",
                    }}>
                        {soNo}
                    </span>
                </div>
            </div>

            {/* ── META FIELDS ── */}
            <div style={{ display: "flex", justifyContent: "space-between", padding: "0 24px 8px", borderBottom: "0.5px solid #ccc" }}>
                <table style={{ borderCollapse: "collapse", fontSize: "11px", flex: 1 }}>
                    <tbody>
                        {[
                            ["Ordered by:", fScrap.customerName(data)],
                            ["Address:",    soOverrides?.address  || ""],
                            ["Salesman:",   soOverrides?.salesman || ""],
                        ].map(([label, val]) => (
                            <tr key={label}>
                                <td style={{ padding: "2px 10px 2px 0", color: "#555", fontWeight: "700", whiteSpace: "nowrap", fontFamily: "Arial, sans-serif" }}>{label}</td>
                                <td style={{ fontWeight: "700", color: "#000", borderBottom: "1px solid #000", minWidth: "160px", paddingBottom: "1px" }}>
                                    {val || "\u00A0"}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                <div style={{ textAlign: "right", fontSize: "11px", paddingTop: "4px", fontFamily: "Arial, sans-serif" }}>
                    <span style={{ color: "#555", fontWeight: "700" }}>Date: </span>
                    <span style={{ fontWeight: "700", color: "#000" }}>
                        {soDate ? new Date(soDate).toLocaleDateString("en-US", { month: "2-digit", day: "2-digit", year: "numeric" }) : "\u00A0"}
                    </span>
                </div>
            </div>

            {/* ── LINE ITEMS TABLE ── */}
            <div style={{ padding: "8px 24px" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", border: "1px solid #999" }}>
                    <thead>
                        <tr style={{ background: "#1e3a8a" }}>
                            {[
                                { h: "Qty",        align: "right",  w: "55px" },
                                { h: "UOM",        align: "center", w: "40px" },
                                { h: "Item Code",  align: "center", w: "75px" },
                                { h: "DESCRIPTION",align: "left"             },
                                { h: "Unit Price", align: "right",  w: "75px" },
                                { h: "Amount",     align: "right",  w: "80px" },
                            ].map(({ h, align, w }) => (
                                <th key={h} style={{
                                    padding: "5px 7px", color: "#fff",
                                    fontSize: "9px", fontWeight: "700",
                                    textTransform: "uppercase", letterSpacing: "0.04em",
                                    textAlign: align, width: w || "auto",
                                    borderRight: "0.5px solid rgba(255,255,255,0.3)",
                                    fontFamily: "Arial, sans-serif",
                                }}>{h}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {displayItems.map((item, i) => {
                            const amt = parseFloat(item.amount) ||
                                (parseFloat(item.qty) * parseFloat(item.unitPrice)) || null;
                            const bg  = i % 2 === 0 ? "#fff" : "#f0fdf4";
                            return (
                                <tr key={i} style={{ borderBottom: "0.5px solid #d1d5db", background: bg, minHeight: "20px" }}>
                                    <td style={{ padding: "4px 7px", textAlign: "right",  fontSize: "10px", fontWeight: "600", color: "#000", borderRight: "0.5px solid #e5e7eb", minHeight: "20px" }}>{item.qty   ?? "\u00A0"}</td>
                                    <td style={{ padding: "4px 7px", textAlign: "center", fontSize: "9px",  color: "#065f46",                  borderRight: "0.5px solid #e5e7eb" }}>{item.uom      || "\u00A0"}</td>
                                    <td style={{ padding: "4px 7px", textAlign: "center", fontSize: "9px",  color: "#374151", fontFamily: "monospace", borderRight: "0.5px solid #e5e7eb" }}>{item.itemCode || "\u00A0"}</td>
                                    <td style={{ padding: "4px 8px", fontSize: "10px",    fontWeight: item.description ? "700" : "400", color: "#000", borderRight: "0.5px solid #e5e7eb" }}>{item.description || "\u00A0"}</td>
                                    <td style={{ padding: "4px 7px", textAlign: "right",  fontSize: "10px", color: "#374151",                  borderRight: "0.5px solid #e5e7eb" }}>{item.unitPrice ? fmt(item.unitPrice) : "\u00A0"}</td>
                                    <td style={{ padding: "4px 7px", textAlign: "right",  fontSize: "10px", fontWeight: "700", color: "#000" }}>{amt ? fmt(amt) : "\u00A0"}</td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {/* ── TOTALS ── */}
            <div style={{ display: "flex", justifyContent: "flex-end", padding: "0 24px 10px" }}>
                <table style={{ borderCollapse: "collapse", fontSize: "10px", minWidth: "220px", fontFamily: "Arial, sans-serif" }}>
                    {[
                        ["TOTAL SALES:",          fmt(totalSales),   false],
                        ["DISCOUNT:",             discount ? fmt(discount) : "—", false],
                        ["TOTAL AMOUNT PAYABLE:", fmt(totalPayable), true],
                    ].map(([label, val, bold]) => (
                        <tr key={label} style={bold ? { background: "#f0fdf4" } : {}}>
                            <td style={{ padding: "3px 10px 3px 0", color: bold ? "#166534" : "#555", fontWeight: "700", textAlign: "right", whiteSpace: "nowrap" }}>{label}</td>
                            <td style={{ padding: "3px 0", textAlign: "right", fontWeight: bold ? "900" : "400", color: bold ? "#166534" : "#000", fontSize: bold ? "12px" : "10px", borderBottom: bold ? "2px solid #166534" : "0.5px solid #e5e7eb", minWidth: "110px" }}>{val}</td>
                        </tr>
                    ))}
                </table>
            </div>

            {/* ── REF / SO REF ── */}
            <div style={{ padding: "0 24px 8px", display: "flex", gap: "24px", fontSize: "10px", color: "#555", fontFamily: "Arial, sans-serif" }}>
                <span><strong>Ref. #</strong> {soOverrides?.refNo || "—"}</span>
                <span><strong>SO #</strong>   {soOverrides?.soRef || "—"}</span>
            </div>

            {/* ── SIGNATURES ── */}
            <div style={{ display: "flex", padding: "8px 24px 14px", borderTop: "0.5px solid #e5e7eb", fontFamily: "Arial, sans-serif" }}>
                {[
                    ["PREPARED BY",  fScrap.preparedBy(data)],
                    ["Confirmed by", fScrap.checkedBy(data)],
                    ["Received by",  fScrap.receivedBy(data)],
                ].map(([label, val]) => (
                    <div key={label} style={{ flex: 1, textAlign: "center" }}>
                        <div style={{
                            fontSize: "11px", fontWeight: "900", color: "#000",
                            borderBottom: "1.5px solid #000", paddingBottom: "3px",
                            minHeight: "26px", display: "inline-block", minWidth: "120px",
                        }}>{val || "\u00A0"}</div>
                        <div style={{ fontSize: "7.5px", color: "#94a3b8", marginTop: "3px", fontStyle: "italic" }}>{label}:</div>
                    </div>
                ))}
            </div>

            {/* ── FOOTER ── */}
            <div style={{ padding: "6px 24px", borderTop: "0.5px solid #e5e7eb", background: "#f8fafc", textAlign: "center" }}>
                <p style={{ fontSize: "7px", color: "#94a3b8", fontStyle: "italic", margin: 0 }}>
                    This document is a property of Mega Soft Hygienic Products, Inc. Unauthorized reproduction is strictly prohibited.
                </p>
            </div>
        </div>
    );
};

// ── PDF Export ────────────────────────────────────────────────────────────────
const exportSOPDF = async (data, soOverrides) => {
    if (!window.html2pdf) {
        await new Promise((resolve, reject) => {
            const s = document.createElement("script");
            s.src = "https://cdnjs.cloudflare.com/ajax/libs/html2pdf.js/0.10.1/html2pdf.bundle.min.js";
            s.onload = resolve; s.onerror = reject;
            document.head.appendChild(s);
        });
    }

    const soNo   = soOverrides?.soNo || formatSoNo(fScrap.controlNo(data));
    const items  = fScrap.items(data).map((item) => ({
        qty:         item.quantity    || item.Quantity    || "",
        uom:         item.uom         || item.Uom         || item.UOM || "KGS",
        itemCode:    item.scrapCodeName || item.ScrapCodeName || "",
        description: item.scrapCodeName || item.ScrapCodeName || "",
        unitPrice:   item.unitPrice   || item.UnitPrice   || "",
        amount:      item.amount      || item.Amount      ||
                     (parseFloat(item.quantity) * parseFloat(item.unitPrice)) || "",
    }));

    const { totalSales, discount, totalPayable } = computeSoTotals(items);
    const MIN_ROWS = 8;
    const displayItems = [
        ...items,
        ...Array.from({ length: Math.max(0, MIN_ROWS - items.length) }, () => ({})),
    ];

    const rowsHTML = displayItems.map((item, i) => {
        const bg  = i % 2 === 0 ? "#fff" : "#f0fdf4";
        const amt = parseFloat(item.amount) || (parseFloat(item.qty) * parseFloat(item.unitPrice)) || 0;
        return `<tr style="background:${bg};border-bottom:0.5px solid #d1d5db">
            <td style="padding:3px 6px;text-align:right;border-right:0.5px solid #e5e7eb;font-size:9px;font-weight:600">${esc(String(item.qty ?? ""))}</td>
            <td style="padding:3px 5px;text-align:center;border-right:0.5px solid #e5e7eb;font-size:8px;color:#065f46">${esc(item.uom || "")}</td>
            <td style="padding:3px 5px;text-align:center;border-right:0.5px solid #e5e7eb;font-size:8px;color:#374151">${esc(item.itemCode || "")}</td>
            <td style="padding:3px 7px;border-right:0.5px solid #e5e7eb;font-size:9px;font-weight:${item.description ? "700" : "400"}">${esc(item.description || "")}</td>
            <td style="padding:3px 6px;text-align:right;border-right:0.5px solid #e5e7eb;font-size:9px">${item.unitPrice ? fmt(item.unitPrice) : ""}</td>
            <td style="padding:3px 6px;text-align:right;font-size:9px;font-weight:700">${amt ? fmt(amt) : ""}</td>
        </tr>`;
    }).join("");

    const soDate = soOverrides?.soDate || fScrap.formDate(data);
    const html = `
    <div style="font-family:'Times New Roman',Times,serif;width:160mm;padding:5mm 7mm;background:#fff;border:1px solid #ccc">
        <div style="text-align:center;padding-bottom:5px;border-bottom:2px solid #000;margin-bottom:6px">
            <div style="font-size:11px;font-weight:900;letter-spacing:0.08em;text-transform:uppercase">MEGA SOFT HYGIENIC PRODUCTS, INC.</div>
        </div>
        <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:6px">
            <div style="font-size:19px;font-weight:900;font-style:italic">SALES ORDER</div>
            <div style="display:flex;align-items:center;gap:4px">
                <span style="font-size:9px;font-weight:700;color:#555">SO No</span>
                <span style="font-size:9px;font-weight:900;color:#166534">VS</span>
                <span style="font-size:20px;font-weight:900;color:#166534;letter-spacing:2px;font-family:monospace">${esc(soNo)}</span>
            </div>
        </div>
        <div style="display:flex;justify-content:space-between;margin-bottom:6px;border-bottom:0.5px solid #ccc;padding-bottom:4px">
            <table style="border-collapse:collapse;font-size:9px">
                <tr><td style="padding:1px 8px 1px 0;color:#555;font-weight:700">Ordered by:</td><td style="font-weight:700;border-bottom:1px solid #000;min-width:120px">${esc(fScrap.customerName(data))}</td></tr>
                <tr><td style="padding:1px 8px 1px 0;color:#555;font-weight:700">Address:</td>  <td style="border-bottom:1px dotted #ccc;min-width:120px">${esc(soOverrides?.address  || "")}</td></tr>
                <tr><td style="padding:1px 8px 1px 0;color:#555;font-weight:700">Salesman:</td> <td style="border-bottom:1px dotted #ccc;min-width:120px">${esc(soOverrides?.salesman || "")}</td></tr>
            </table>
            <div style="font-size:9px;text-align:right;padding-top:2px">
                <span style="color:#555;font-weight:700">Date: </span>
                <span style="font-weight:700">${soDate ? new Date(soDate).toLocaleDateString("en-US", { month:"2-digit", day:"2-digit", year:"numeric" }) : ""}</span>
            </div>
        </div>
        <table style="width:100%;border-collapse:collapse;border:1px solid #999;margin-bottom:6px">
            <thead>
                <tr style="background:#1e3a8a">
                    <th style="padding:4px 6px;color:#fff;font-size:8px;text-align:right;width:50px;border-right:0.5px solid rgba(255,255,255,0.3)">Qty</th>
                    <th style="padding:4px 5px;color:#fff;font-size:8px;text-align:center;width:36px;border-right:0.5px solid rgba(255,255,255,0.3)">UOM</th>
                    <th style="padding:4px 5px;color:#fff;font-size:8px;text-align:center;width:65px;border-right:0.5px solid rgba(255,255,255,0.3)">Item Code</th>
                    <th style="padding:4px 7px;color:#fff;font-size:8px;text-align:left;border-right:0.5px solid rgba(255,255,255,0.3)">DESCRIPTION</th>
                    <th style="padding:4px 6px;color:#fff;font-size:8px;text-align:right;width:65px;border-right:0.5px solid rgba(255,255,255,0.3)">Unit Price</th>
                    <th style="padding:4px 6px;color:#fff;font-size:8px;text-align:right;width:70px">Amount</th>
                </tr>
            </thead>
            <tbody>${rowsHTML}</tbody>
        </table>
        <div style="display:flex;justify-content:flex-end;margin-bottom:8px">
            <table style="border-collapse:collapse;font-size:9px;min-width:180px">
                <tr><td style="padding:2px 10px 2px 0;color:#555;font-weight:700;text-align:right">TOTAL SALES:</td><td style="text-align:right;font-weight:700;border-bottom:0.5px solid #e5e7eb;min-width:90px">${fmt(totalSales)}</td></tr>
                <tr><td style="padding:2px 10px 2px 0;color:#555;font-weight:700;text-align:right">DISCOUNT:</td><td style="text-align:right;border-bottom:0.5px solid #e5e7eb">${discount ? fmt(discount) : "—"}</td></tr>
                <tr style="background:#f0fdf4"><td style="padding:3px 10px 3px 0;color:#166534;font-weight:900;text-align:right">TOTAL AMOUNT PAYABLE:</td><td style="text-align:right;font-weight:900;color:#166534;font-size:10px;border-bottom:2px solid #166534">${fmt(totalPayable)}</td></tr>
            </table>
        </div>
        <div style="font-size:8px;color:#555;margin-bottom:8px">
            <span style="font-weight:700">Ref. # </span>${esc(soOverrides?.refNo || "—")}&nbsp;&nbsp;
            <span style="font-weight:700">SO # </span>${esc(soOverrides?.soRef || "—")}
        </div>
        <div style="display:flex;border-top:0.5px solid #e5e7eb;padding-top:6px">
            ${[["PREPARED BY", fScrap.preparedBy(data)], ["Confirmed by", fScrap.checkedBy(data)], ["Received by", fScrap.receivedBy(data)]].map(([label, val]) =>
                `<div style="flex:1;text-align:center">
                    <div style="font-size:9px;font-weight:900;border-bottom:1px solid #000;padding-bottom:2px;min-height:18px;display:inline-block;min-width:90px">${esc(val || "")}</div>
                    <div style="font-size:6.5px;color:#94a3b8;margin-top:2px;font-style:italic">${label}:</div>
                </div>`).join("")}
        </div>
        <div style="margin-top:5px;border-top:0.5px solid #e5e7eb;text-align:center;padding-top:3px">
            <p style="font-size:6px;color:#94a3b8;font-style:italic;margin:0">This document is a property of Mega Soft Hygienic Products, Inc. Unauthorized reproduction is strictly prohibited.</p>
        </div>
    </div>`;

    const el = document.createElement("div");
    el.innerHTML = html;
    document.body.appendChild(el);
    try {
        await window.html2pdf(el, {
            margin: [4, 4, 4, 4],
            filename: `SalesOrder_VS${soNo}.pdf`,
            image: { type: "jpeg", quality: 0.98 },
            html2canvas: { scale: 2, useCORS: true, logging: false },
            jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
        });
    } finally {
        document.body.removeChild(el);
    }
};

// ════════════════════════════════════════════════════════════════════════════
// ── MAIN MODAL COMPONENT ──────────────────────────────────────────────────
// ════════════════════════════════════════════════════════════════════════════
const ViewSOModal = ({ row, onClose }) => {
    const [pdfLoading, setPdfLoading] = useState(false);

    // Optional: editable SO fields (these don't exist on a Scrap form but the SO needs them)
    const [soOverrides, setSoOverrides] = useState({
        soNo:     formatSoNo(fScrap.controlNo(row)),
        soDate:   fScrap.formDate(row),
        address:  "",
        salesman: "",
        refNo:    "",
        soRef:    "",
    });

    if (!row) return null;

    const gradHdr = { background: "linear-gradient(135deg, #0c1445 0%, #1a2f7a 50%, #1e40af 100%)" };

    const handleExportPDF = async () => {
        setPdfLoading(true);
        try { await exportSOPDF(row, soOverrides); }
        catch (err) { alert("PDF Export Failed: " + err.message); }
        finally { setPdfLoading(false); }
    };

    return (
        <div
            className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-[60] p-4"
            onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
        >
            <div
                className="bg-white rounded-3xl w-full max-w-4xl max-h-[95vh] flex flex-col overflow-hidden"
                style={{ boxShadow: "0 32px 80px rgba(0,0,0,0.45)" }}
            >
                {/* ── Modal Header ── */}
                <div style={gradHdr} className="px-7 py-5 flex items-center justify-between flex-shrink-0">
                    <div className="flex items-center gap-4">
                        <div className="bg-white/15 border border-white/20 p-2.5 rounded-2xl">
                            <ShoppingCart size={20} className="text-white" />
                        </div>
                        <div>
                            <p className="text-blue-300 text-[10px] font-bold uppercase tracking-[0.2em] mb-0.5">
                                Mega Soft · Sales Management
                            </p>
                            <h2 className="text-white font-extrabold text-base leading-none">Sales Order</h2>
                        </div>
                    </div>
                    <div className="flex items-center gap-3">
                        <div className="text-right bg-white/10 border border-white/20 rounded-xl px-4 py-2">
                            <p className="text-blue-300 text-[9px] font-bold uppercase tracking-widest">SO No.</p>
                            <p className="text-white font-extrabold text-sm tracking-wider font-mono">
                                <span className="text-green-300">VS</span>{soOverrides.soNo}
                            </p>
                        </div>
                        <button
                            onClick={onClose}
                            className="bg-white/15 hover:bg-white/25 text-white rounded-xl w-9 h-9 flex items-center justify-center transition-all cursor-pointer"
                            style={{ border: "none" }}
                        >
                            <X size={16} />
                        </button>
                    </div>
                </div>

                {/* ── Optional SO-specific fields (address, salesman, ref) ── */}
                <div className="px-7 py-3 bg-slate-800 flex flex-wrap gap-4 items-end flex-shrink-0">
                    {[
                        { key: "address",  label: "Address",   placeholder: "Customer address" },
                        { key: "salesman", label: "Salesman",  placeholder: "Salesman name" },
                        { key: "refNo",    label: "Ref. #",    placeholder: "e.g. 1372257" },
                        { key: "soRef",    label: "SO Ref #",  placeholder: "e.g. 7930 7931" },
                    ].map(({ key, label, placeholder }) => (
                        <div key={key} className="flex flex-col gap-1 flex-1 min-w-[120px]">
                            <label className="text-[9px] font-bold uppercase tracking-widest text-slate-400">{label}</label>
                            <input
                                type="text"
                                value={soOverrides[key]}
                                placeholder={placeholder}
                                onChange={(e) => setSoOverrides((p) => ({ ...p, [key]: e.target.value }))}
                                className="border border-slate-600 bg-slate-700 text-white text-xs rounded-lg px-2.5 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-400 placeholder-slate-500"
                            />
                        </div>
                    ))}
                </div>

                {/* ── Document Preview ── */}
                <div className="flex-1 overflow-y-auto" style={{ background: "#e8edf5" }}>
                    <div className="p-6">
                        <div
                            className="bg-white rounded-2xl overflow-hidden"
                            style={{ boxShadow: "0 8px 32px rgba(0,0,0,0.12)" }}
                        >
                            <SalesOrderDocument data={row} soOverrides={soOverrides} />
                        </div>
                    </div>
                </div>

                {/* ── Footer ── */}
                <div
                    className="px-7 py-4 bg-white border-t border-gray-100 flex items-center justify-between flex-shrink-0"
                    style={{ boxShadow: "0 -4px 16px rgba(0,0,0,0.06)" }}
                >
                    <button
                        onClick={handleExportPDF}
                        disabled={pdfLoading}
                        className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-white text-sm font-bold disabled:opacity-50 transition-all cursor-pointer hover:-translate-y-0.5"
                        style={{
                            background: "linear-gradient(135deg,#dc2626,#b91c1c)",
                            boxShadow: "0 2px 10px rgba(220,38,38,0.35)",
                            border: "none",
                        }}
                    >
                        <Download size={15} />
                        {pdfLoading ? "Generating PDF..." : "Export SO PDF"}
                    </button>
                    <button
                        onClick={onClose}
                        className="flex items-center gap-2 px-5 py-2.5 rounded-xl border-2 border-gray-200 bg-gray-50 hover:bg-gray-100 text-gray-600 text-sm font-bold transition-all cursor-pointer"
                    >
                        <X size={14} /> Close
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ViewSOModal;

// ════════════════════════════════════════════════════════════════════════════
// HOW TO INTEGRATE INTO ScrapLoadingForm.jsx
// ════════════════════════════════════════════════════════════════════════════
//
// 1. Import at the top of ScrapLoadingForm.jsx:
//    import ViewSOModal from "./ViewSOModal";
//
// 2. Add state near your other useState declarations:
//    const [selectedSORow, setSelectedSORow] = useState(null);
//
// 3. Add "View SO" button in your Actions column (next to View / Delete):
//    <button
//      onClick={() => setSelectedSORow(row)}
//      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-300
//                 bg-white text-gray-600 text-xs font-semibold hover:bg-green-50 hover:border-green-300
//                 hover:text-green-700 transition-all cursor-pointer">
//      <FileText size={13} /> View SO
//    </button>
//
// 4. Add the modal before the closing </div> of your component return:
//    {selectedSORow && (
//      <ViewSOModal row={selectedSORow} onClose={() => setSelectedSORow(null)} />
//    )}